import { useEffect, useState } from "react";
import { closeOverlay, issueRecoveryCode } from "../../app/actions.ts";
import { useApp } from "../../app/store.ts";
import { vi } from "../../i18n/vi.ts";
import { platform } from "../../services/platform.ts";
import {
  absoluteUrl,
  applyNativeBundle,
  applyUpdate,
  checkNativeUpdate,
  dismissUpdate,
  downloadApk,
  fetchRelease,
  useNativeUpdateStatus,
  type AndroidRelease,
} from "../../services/update/updates.ts";
import { Art, Button, QrCode, Sheet } from "../components/common.tsx";

/** Small card in the city when an update is ready; never shown during a level. */
export function UpdateBanner() {
  const update = useApp((s) => s.ui.update);
  const [busy, setBusy] = useState(false);
  if (!update) return null;
  const apk = update.kind === "apk";
  return (
    <div className="update-card" role="status" data-testid="update-banner">
      <Art id="icon.update" className="update-card__icon" />
      <div className="update-card__text">
        <strong>{apk ? vi.update.apk : vi.update.ready}</strong>
        {apk ? <small>{vi.update.apkBody}</small> : null}
      </div>
      <div className="update-card__actions">
        <button
          type="button"
          className="update-card__go"
          disabled={busy}
          onClick={() => {
            setBusy(true);
            void applyUpdate().finally(() => setBusy(false));
          }}
        >
          {apk ? vi.update.download : vi.update.apply}
        </button>
        <button type="button" className="update-card__later" onClick={() => dismissUpdate()}>
          {vi.update.later}
        </button>
      </div>
    </div>
  );
}

/** Settings inside the APK: asks the server on open, shows the download and applies it on a tap. */
export function NativeUpdates() {
  const status = useNativeUpdateStatus();
  const online = useApp((s) => s.ui.online);
  const busy = status.phase === "checking" || status.phase === "downloading";
  useEffect(() => {
    if (online) void checkNativeUpdate();
  }, [online]);
  const line =
    status.phase === "checking"
      ? vi.update.checking
      : status.phase === "downloading"
        ? vi.update.downloading
        : status.phase === "failed"
          ? vi.update.failed
          : status.apk
            ? vi.update.apkReady(status.apk)
            : status.bundle
              ? vi.update.readyBody
              : status.phase === "done"
                ? vi.update.latest
                : null;
  return (
    <div className="update-panel" data-testid="native-updates">
      {line ? (
        <p className="update-panel__status" role="status">
          <Art id="icon.update" className="ico-inline" />
          {line}
        </p>
      ) : null}
      {status.phase === "downloading" ? (
        <div className="update-panel__bar" role="progressbar" aria-valuenow={Math.round(status.progress * 100)}>
          <span style={{ width: `${Math.max(4, status.progress * 100)}%` }} />
        </div>
      ) : null}
      {status.apk ? (
        <>
          <p className="note">{vi.update.apkBody}</p>
          <Button variant="gold" onClick={() => void downloadApk()}>
            <Art id="icon.download" className="btn__icon" />
            {vi.update.download}
          </Button>
        </>
      ) : null}
      {status.bundle && !busy ? (
        <Button variant="gold" onClick={() => void applyNativeBundle()} testId="apply-update">
          {vi.update.applyNow}
        </Button>
      ) : null}
      <Button variant="secondary" disabled={!online || busy} onClick={() => void checkNativeUpdate()} testId="check-update">
        {vi.update.check}
      </Button>
      <p className="note">{vi.update.auto}</p>
    </div>
  );
}

function megabytes(bytes: number): string {
  return bytes > 0 ? `${(bytes / 1024 / 1024).toFixed(1)} MB` : "";
}

/** Android browser players: download the APK and carry the journey over with a recovery code. */
export function GetApp() {
  const [release, setRelease] = useState<AndroidRelease | null | undefined>(undefined);
  const [code, setCode] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const online = useApp((s) => s.ui.online);
  useEffect(() => {
    let alive = true;
    void fetchRelease().then((value) => alive && setRelease(value));
    return () => {
      alive = false;
    };
  }, []);
  const apk = release?.apk ?? null;
  // On a computer the phone scans the link; on an Android phone the button downloads directly.
  const scan = !platform.androidWeb();
  return (
    <Sheet title={vi.getApp.title} onClose={() => closeOverlay()} className="get-app" testId="get-app">
      <div className="get-app__hero">
        <Art id="icon.download" className="get-app__icon" />
        <p>{vi.getApp.body}</p>
      </div>
      <ol className="steps">
        <li>
          <span>{vi.getApp.step1}</span>
          {code ? (
            <code className="recovery__code" data-testid="get-app-code">
              {code}
            </code>
          ) : (
            <Button
              variant="secondary"
              disabled={busy || !online}
              onClick={async () => {
                setBusy(true);
                setCode(await issueRecoveryCode());
                setBusy(false);
              }}
            >
              {vi.getApp.makeCode}
            </Button>
          )}
        </li>
        <li>
          <span>{vi.getApp.step2}</span>
          {apk ? (
            <>
              {scan ? (
                <>
                  <p className="note">{vi.getApp.scan}</p>
                  <QrCode text={absoluteUrl(apk.url)} className="get-app__qr" />
                </>
              ) : null}
              <a className="btn btn--gold" href={absoluteUrl(apk.url)} download data-testid="get-app-download">
                <Art id="icon.download" className="btn__icon" />
                <span className="btn__label">{vi.getApp.download(megabytes(apk.size))}</span>
              </a>
              {scan ? <p className="note">{vi.getApp.android}</p> : null}
            </>
          ) : release === undefined ? (
            <p className="note">{vi.wait}</p>
          ) : (
            <p className="note">{vi.getApp.unavailable}</p>
          )}
        </li>
        <li>
          <span>{vi.getApp.step3}</span>
        </li>
      </ol>
      {apk ? <p className="note">v{apk.versionName}</p> : null}
    </Sheet>
  );
}
